import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    CircularProgress,
    Grid,
    FormControl,
    Typography,
    TextField
} from "@mui/material/";
import actions from "../../../Store/actions";
import { strings } from "../../i18n";
import { helpers } from "../../lib";
import BranchSelect from "../BranchSelect";
import { debounce } from "lodash";
import styles from "./styles";
import { logToSentry } from "../../../config/Sentry";       

type Props = {
    onSubmit: Function
};
/**
 * Location Form Component
 */
const initialGeoLocation = {
    latitude: 0,
    longitude: 0
};
const delay = 800;

export default ({ onSubmit }: Props) => {
    const [branchIds, setBranchIds] = useState([]);
    const [locName, setLocationName] = useState("");
    const [geoLocation, setGeoLocation] = useState(initialGeoLocation);
    const [oldGeoLocation, setOldGeoLocation] = useState(initialGeoLocation);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const { branchLocations, locations } = useSelector((state) => state);
    const dispatch = useDispatch();
    const [refs, setRefs] = useState("");
    const { item } = locations;

    useEffect(() => {
        if (item && item.id) {
            const next = branchLocations.data
                .filter(({ location }) => location === item.id)
                .map(({ branch }) => branch);
            setBranchIds(next);
            if (item.geo && item.geo.length > 0) {
                const geo = {
                    ...geoLocation,
                    longitude: item.geo[0].longitude,
                    latitude: item.geo[0].latitude
                };
                setGeoLocation(geo);
                setOldGeoLocation(geo);
            }
        }
    }, [branchLocations, locations, item.id]);

    /** On locationRef name join */
    useEffect(() => {
        setRefs(helpers.nameJoin(item.locationRef, "\n"));
    }, [item.locationRef]);

    /** On name change */
    useEffect(() => {
        setLocationName(item.name);
    }, [item.name]);

    const debounceValueChange = useCallback(
        debounce((value: string) => {
            dispatch(actions.locations.create.itemUpdate({ name: value }));
        }, delay),
        [delay]
    );

    const onChange = useCallback(
        (event: Event) => {
            const { value } = event.target;
            setLocationName(value);
            debounceValueChange(value);
        },
        [debounceValueChange]
    );

    const onRefsBlur = () => {
        const locations = refs.replace(/(^[ \t]+|[ \t]+(?=\n)|^\s*$\n?)/gm, "");
        const loc = helpers.nameSplit(locations, "\n");
        const uniqueRef = new Set();
        const locationRef = [];

        /** Remove duplicate */
        for (const obj of loc) {
            if (!uniqueRef.has(obj.name)) {
                locationRef.push(obj);
                uniqueRef.add(obj.name);
            }
        }
        dispatch(actions.locations.create.itemUpdate({ locationRef }));
    };

    const onRefsChange = (event: Event) => {
        const { value } = event.target;
        setRefs(value);
    };

    const onGeoChange = (evt: Event) => {
        const { name, value } = evt.target;
        setGeoLocation((geoLocation) => {
            const modifiedValue = { ...geoLocation, [name]: value };
            onValidateLatLng(modifiedValue);
            return modifiedValue;
        });
    };

    const onValidateLatLng = (geoLocation) => {
        const { latitude, longitude } = geoLocation;
        if (latitude.length === 0 || longitude.length === 0) {
            setError(strings("messages.geoEmpty"));
        } else if (!(isFinite(latitude) && Math.abs(latitude) <= 90)) {
            setError(strings("messages.latitudeErrorMessage"));
        } else if (!(isFinite(longitude) && Math.abs(longitude) <= 180)) {
            setError(strings("messages.longitudeErrorMessage"));
        } else setError("");
    };

    const onSave = async () => {
        try {
            const isUpdate = Boolean(item.id);
            let next = [];

            setLoading(true);

            if (isUpdate) {
                const { id, name, geo } = item;

                if (geo.length > 0) {
                    geo[0].latitude = parseFloat(geoLocation.latitude);
                    geo[0].longitude = parseFloat(geoLocation.longitude);
                } else {
                    item.geo = [geoLocation];
                }
                const res = await dispatch(
                    actions.locations.create.patchRequest({ id, name })
                );
                next = [{ ...item, branch: [...branchIds], ...res.location }];
            } else {
                next = [{ ...item, branch: [...branchIds] }];
            }
            await dispatch(actions.locations.create.postRequest(next));
            const message = strings(
                isUpdate ? "updatedSuccessfully" : "createdSuccessfully"
            );
            dispatch(actions.toast.create.success(message));
            onSubmit && onSubmit();
            // await dispatch(actions.locations.create.getRequest());
        } catch (error) {
            logToSentry("On location save:", { error });
            console.error(error);
        }
        setLoading(false);
    };

    return (
        <form>
            <FormControl fullWidth margin="normal">
                <TextField
                    variant="standard"
                    label={strings("name")}
                    onChange={onChange}
                    value={locName}
                />
                <FormControl margin="normal">
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                variant="standard"
                                size="small"
                                label={strings("latitude")}
                                onChange={onGeoChange}
                                name="latitude"
                                value={oldGeoLocation.latitude}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                variant="standard"
                                size="small"
                                label={strings("latitude")}
                                onChange={onGeoChange}
                                name="latitude"
                                value={geoLocation.latitude}
                            />
                        </Grid>
                    </Grid>
                </FormControl>
                <FormControl fullWidth margin="normal">
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                variant="standard"
                                label={strings("longitude")}
                                name="longitude"
                                onChange={onGeoChange}
                                value={oldGeoLocation.longitude}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                variant="standard"
                                label={strings("longitude")}
                                name="longitude"
                                onChange={onGeoChange}
                                value={geoLocation.longitude}
                            />
                        </Grid>
                        {error.length > 0 ? (
                            <Grid item xs={12}>
                                <Typography
                                    variant="standard"
                                    align="center"
                                    component="div"
                                    style={{ color: "red" }}
                                >
                                    {error}
                                </Typography>
                            </Grid>
                        ) : null}
                    </Grid>
                </FormControl>
                <TextField
                    variant="standard"
                    label={strings("refs")}
                    multiline
                    onBlur={onRefsBlur}
                    onChange={onRefsChange}
                    minRows={5}
                    maxRows={5}
                    value={refs}
                />
            </FormControl>
            <FormControl fullWidth margin="normal">
                <BranchSelect
                    defaultValue={branchIds}
                    onChangeVal={setBranchIds}
                    multiple
                />
            </FormControl>
            <FormControl fullWidth margin="normal">
                <Button
                    sx={styles.primaryButton}
                    variant="contained"
                    size="medium"
                    disabled={loading || error.length > 0 || !item.name || branchIds.length === 0}
                    onClick={onSave}
                >
                    {loading ? <CircularProgress size={24} /> : strings("save")}
                </Button>
            </FormControl>
        </form>
    );
};
